import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  Dialog,
  Box,
  Stack,
  SelectableList,
  Alert,
  Text,
  TextLink,
  useToast
} from '@machobear/design-system'
import { calc } from '@vanilla-extract/css-utils'
import queryString from 'query-string'
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query'
import { vars } from '@machobear/design-system/css'
import useProfile from '@/hooks/use-profile'
import client from '@/ports/api-client'

import { useDebounce } from '@/utils/debounce'
import config from '@/config'
import { LoadingCircle } from '@/components/loading-circle'
import EmptyDetachedList from './EmptyDetachedList'
import ListingsSearchField from './ListingsSearchField'
import { Listing, ModalListingsProps } from '../helpers/types'
import { transformListingData } from '../helpers/utils'

type OnAttachListingsType = (selectedListingIds: string[]) => void

type Props = Pick<ModalListingsProps, 'visible' | 'contentID' | 'page' | 'closeModal'> & {
  onAttachListings: OnAttachListingsType
}

type Data = {
  items: Listing[]
  total_items: number
  limit: number
  first: number
  previous: number
  current: number
  next: number
  last: number
}

type DescriptionProps = {
  id: string
  data: InfiniteData<Data> | undefined
  isLoading: boolean
  closeModal: () => void
  searchString: string
  setSearchString: Dispatch<SetStateAction<string>>
  fetchNextPage: () => void
  hasNextPage: boolean
  filters: string[]
  setFilter: Dispatch<SetStateAction<string[]>>
  isFetchingNextPage: boolean
  onAttachListings: OnAttachListingsType
}

const getDetachedListings = async (
  id: string,
  page?: number,
  filter?: string[],
  search?: string
) => {
  const filterString = filter ? filter.join(',') : ''
  const queryParams = queryString.stringify({
    page: page,
    filter: filterString,
    search: search
  })

  const url = `/auto_reply_trigger/content/listings/detached/search/${id}?per_page=100${
    queryParams ? `&${queryParams}` : ''
  }`
  return client.get(url)
}

const { WEB_URL } = config

const AssignListingsModal = ({ visible, contentID, closeModal, onAttachListings }: Props) => {
  const { t } = useTranslation()
  const [searchString, setSearchString] = useState<string>('')
  const [filter, setFilter] = useState<string[]>([])
  const debouncedSearch = useDebounce(searchString, 700)

  const { fetchNextPage, hasNextPage, data, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['autoreply', 'autoreply_detached_listings', contentID, debouncedSearch, filter],
    queryFn: ({ pageParam }) => getDetachedListings(contentID, pageParam, filter, debouncedSearch),
    initialPageParam: 1,
    enabled: !!contentID && (!!searchString || !!filter),
    getNextPageParam: lastPage => {
      return !lastPage.total_items || lastPage.current === lastPage.last ? null : lastPage.next
    }
  })
  const handleOnCloseAssignListings = () => {
    closeModal()
  }

  return (
    <Dialog
      open={visible}
      onClose={handleOnCloseAssignListings}
      title={t('l.settings.view.auto_reply.assign_listings', 'Assign Listings')}
      id="assign-listings"
      width="medium"
    >
      <AssignListingsDescription
        onAttachListings={onAttachListings}
        data={data}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        id={contentID}
        fetchNextPage={fetchNextPage}
        closeModal={closeModal}
        searchString={searchString}
        setSearchString={setSearchString}
        hasNextPage={hasNextPage}
        setFilter={setFilter}
        filters={filter}
      />
    </Dialog>
  )
}

const AssignListingsDescription = ({
  data,
  isLoading,
  isFetchingNextPage,
  id,
  closeModal,
  searchString,
  setSearchString,
  fetchNextPage,
  hasNextPage,
  filters,
  onAttachListings,
  setFilter
}: DescriptionProps) => {
  const { t } = useTranslation()
  const profileData = useProfile()
  const showToast = useToast()
  const isEssentialPlan = profileData?.company_info?.base_plan_name === 'essentials-plan-monthly'
  const listHeight = calc.multiply(100, vars.grid)
  const transformedItems = data?.pages.flatMap(page => page.items).map(transformListingData)
  return (
    <Box marginTop="medium">
      <Stack space="large">
        <ListingsSearchField
          id="SearchInput_assign_listings"
          value={searchString}
          filters={filters}
          onFilter={setFilter}
          onSearch={e => setSearchString(e.target.value)}
        />
        {isEssentialPlan && (
          <Alert tone="promote">
            <Trans i18nKey="l.settings.view.auto_reply.alert_assign_listings" t={t}>
              <Text size="small">
                Want to assign more listings?{' '}
                <TextLink href={`${WEB_URL}/pricing`} target="_blank" weight="regular">
                  Upgrade to Growth Plan
                </TextLink>{' '}
                for unlimited access.
              </Text>
            </Trans>
          </Alert>
        )}
        {isLoading ? (
          <Box
            style={{ height: listHeight }}
            width="full"
            display="flex"
            justifyContent="center"
            padding="xlarge"
          >
            <LoadingCircle />
          </Box>
        ) : (
          <SelectableList
            items={transformedItems || []}
            heading={t('l.settings.view.auto_reply.unit_address', 'Unit Address')}
            itemsSelectedText={t('l.settings.view.auto_reply.items_selected', 'items selected')}
            clearAllText={t('l.settings.view.auto_reply.clear_all', 'Clear all')}
            emptyComponent={<EmptyDetachedList />}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={() => fetchNextPage()}
            submitButton={{
              label: t('l.settings.view.auto_reply.assign_listings', 'Assign Listings'),
              onClick: selectedData => {
                if (isEssentialPlan && selectedData.length > 1) {
                  showToast({
                    tone: 'critical',
                    message: t(
                      'l.settings.view.auto_reply.max_listings_selected_essentials.text',
                      'Too many listings selected, please assign only one listing'
                    )
                  })
                  return
                }
                onAttachListings(selectedData)

                closeModal()
              }
            }}
          />
        )}
      </Stack>
    </Box>
  )
}

export default AssignListingsModal
